import type { InitOptions } from 'i18next';

import resourcesToBackend from 'i18next-resources-to-backend';

export const supportedLngs = ['en', 'ru', 'hy'] as const;
export type LangCode = (typeof supportedLngs)[number];

// Fallback and default namespace
export const fallbackLng: LangCode = 'ru';
export const defaultNS: string | string[] = 'common';

// Storage config
export const storageConfig = {
  cookie: { key: 'sellerSosI18nextLng', autoDetection: false },
  localStorage: { key: 'sellerSosI18nextLng', autoDetection: false },
} as const;

/**
 * @countryCode https://flagcdn.com/en/codes.json
 * @adapterLocale https://github.com/iamkun/dayjs/tree/master/@/locale
 * @numberFormat https://simplelocalize.io/data/locales/
 */

export type LangOption = {
  value: LangCode;
  label: string;
  countryCode: string;
  adapterLocale?: string;
  dayjs?: string;
  numberFormat: { code: string; currency: string };
};

export const allLangs: LangOption[] = [
  {
    value: 'en',
    label: 'English',
    countryCode: 'GB',
    adapterLocale: 'en',
    dayjs: 'en',
    numberFormat: { code: 'en-US', currency: 'USD' },
  },
  {
    value: 'ru',
    label: 'Russian',
    countryCode: 'RU',
    adapterLocale: 'ru',
    dayjs: 'ru',
    numberFormat: { code: 'ru-RU', currency: 'RUB' },
  },
  {
    value: 'hy',
    label: 'Armenian',
    countryCode: 'AM',
    adapterLocale: 'hy',
    dayjs: 'hy-am',
    numberFormat: { code: 'hy-AM', currency: 'AMD' },
  },
];

export const i18nResourceLoader = resourcesToBackend(
  (lang: LangCode, namespace: string) => import(`./langs/${lang}/${namespace}.json`)
);

export function i18nOptions(lang = fallbackLng, namespace = defaultNS): InitOptions {
  return {
    // debug: true,
    supportedLngs,
    fallbackLng,
    lng: lang,
    /********/
    fallbackNS: defaultNS,
    defaultNS,
    ns: namespace,
    detection: {
      order: ['cookie', 'localStorage', 'htmlTag', 'path'],
      caches: ['cookie', 'localStorage'],
      lookupCookie: storageConfig.cookie.key,
      lookupLocalStorage: storageConfig.localStorage.key,
    },
  };
}

export function getCurrentLang(lang?: string): LangOption {
  const fallbackLang = allLangs.find((l) => l.value === fallbackLng) ?? allLangs[0];

  if (!lang) {
    return fallbackLang;
  }

  return allLangs.find((l) => l.value === lang) ?? fallbackLang;
}
